import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiJson } from "../api/client";

import {
  getSelectedCompanyId,
  getSelectedCompanyName,
  isSuperAdmin,
  setSelectedCompany,
} from "../utils/tenantContext";

import "./TenantEnrollmentPage.css";

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.keys)) return value.keys;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

function TenantEnrollmentPage() {
  const [companyId, setCompanyId] = useState(
    getSelectedCompanyId()
  );

  const [companyName, setCompanyName] = useState(
    getSelectedCompanyName()
  );

  const [keys, setKeys] = useState([]);
  const [devices, setDevices] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [maxDevices, setMaxDevices] = useState("50");

  const [oneTimeKey, setOneTimeKey] = useState("");
  const [oneTimeKeyLabel, setOneTimeKeyLabel] =
    useState("");

  const superAdmin = isSuperAdmin();

  const selectedCompanyDevices = useMemo(() => {
    return devices.filter(
      (device) =>
        String(device.company_id || "") ===
        String(companyId || "")
    );
  }, [devices, companyId]);

  const activeDevices = selectedCompanyDevices.filter(
    (device) =>
      String(device.lifecycle_state || "active") ===
      "active"
  );

  const windowsDevices = activeDevices.filter((device) =>
    String(device.platform || "")
      .toLowerCase()
      .startsWith("windows")
  );

  const macDevices = activeDevices.filter((device) => {
    const platform = String(
      device.platform || ""
    ).toLowerCase();

    return (
      platform === "darwin" ||
      platform.startsWith("macos") ||
      platform.startsWith("mac os")
    );
  });

  const loadDevices = useCallback(async () => {
    try {
      const result = await apiJson(
        superAdmin
          ? "/devices"
          : `/devices/company/${companyId}`
      );

      setDevices(
        Array.isArray(result)
          ? result
          : Array.isArray(result?.devices)
          ? result.devices
          : []
      );
    } catch (err) {
      console.error(err);
    }
  }, [companyId, superAdmin]);

  const loadKeys = useCallback(async () => {
    if (!companyId) {
      setKeys([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await apiJson(
        `/enrollment-keys?company_id=${encodeURIComponent(
          companyId
        )}`
      );

      setKeys(normalizeList(result));
    } catch (err) {
      setError(err.message || "Unable to load enrollment keys.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const chooseCompany = () => {
    const id = String(companyId || "").trim();

    if (!id) {
      setError("Enter a company ID.");
      return;
    }

    const matchingDevice = devices.find(
      (device) =>
        String(device.company_id || "") === id
    );

    const discoveredName =
      matchingDevice?.company_name ||
      matchingDevice?.company ||
      companyName ||
      `Company ${id}`;

    setCompanyName(discoveredName);
    setSelectedCompany(id, discoveredName);

    setMessage(
      `${discoveredName} is now the selected dashboard tenant.`
    );

    setError("");
    loadKeys();
  };

  const createKey = async () => {
    if (!companyId) return;

    if (
      !window.confirm(
        `Generate a new enrollment key for company ${companyId}?`
      )
    ) {
      return;
    }

    setMessage("");
    setError("");
    setOneTimeKey("");

    try {
      const payload = {
        company_id: Number(companyId),
        max_devices: Math.max(
          1,
          Number(maxDevices) || 50
        ),
      };

      const result = await apiJson(
        "/enrollment-keys",
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      if (result?.enrollment_key) {
        setOneTimeKey(result.enrollment_key);
        setOneTimeKeyLabel(
          `Enrollment key v${result.version || ""}`
        );
      }

      setMessage(
        "Enrollment key generated. Save the raw key now; the list view stores only its fingerprint."
      );

      await loadKeys();
    } catch (err) {
      setError(err.message || "Key generation failed.");
    }
  };

  const rotateKey = async (key) => {
    if (
      !window.confirm(
        `Rotate enrollment key v${key.version}?`
      )
    ) {
      return;
    }

    setMessage("");
    setError("");
    setOneTimeKey("");

    try {
      const result = await apiJson(
        `/enrollment-keys/${key.id}/rotate`,
        {
          method: "POST",
          body: JSON.stringify({
            company_id: Number(companyId),
            grace_hours: 0,
          }),
        }
      );

      if (result?.enrollment_key) {
        setOneTimeKey(result.enrollment_key);
        setOneTimeKeyLabel(
          `Replacement key v${result.version || ""}`
        );
      }

      setMessage(
        "Enrollment key rotated. The replacement raw key is shown below."
      );

      await loadKeys();
    } catch (err) {
      setError(err.message || "Key rotation failed.");
    }
  };

  const revokeKey = async (key) => {
    if (
      !window.confirm(
        `REVOKE enrollment key v${key.version}? This prevents that enrollment key from being used again.`
      )
    ) {
      return;
    }

    setMessage("");
    setError("");

    try {
      await apiJson(
        `/enrollment-keys/${key.id}/revoke`,
        {
          method: "POST",
          body: JSON.stringify({
            company_id: Number(companyId),
          }),
        }
      );

      setMessage(
        `Enrollment key v${key.version} revoked.`
      );

      await loadKeys();
    } catch (err) {
      setError(err.message || "Key revocation failed.");
    }
  };

  const copyKey = async () => {
    if (!oneTimeKey) return;

    try {
      await navigator.clipboard.writeText(oneTimeKey);
      setMessage("Enrollment key copied.");
    } catch {
      setError(
        "Browser clipboard access failed. Copy the key manually."
      );
    }
  };

  return (
    <div className="tem-page">
      <div className="tem-title-row">
        <div>
          <span className="tem-kicker">
            TENANT SECURITY
          </span>

          <h1>Enrollment Management</h1>

          <p>
            Company enrollment, device ownership and
            credential lifecycle.
          </p>
        </div>

        <div className="tem-badge">
          {superAdmin ? "SUPER ADMIN" : "COMPANY ADMIN"}
        </div>
      </div>

      <section className="tem-card tem-selector">
        <div>
          <span>Selected company</span>

          <strong>
            {companyName ||
              (companyId
                ? `Company ${companyId}`
                : "None")}
          </strong>
        </div>

        {superAdmin && (
          <div className="tem-company-controls">
            <input
              type="number"
              min="1"
              value={companyId}
              onChange={(event) =>
                setCompanyId(event.target.value)
              }
              placeholder="Company ID"
            />

            <button onClick={chooseCompany}>
              Select Company
            </button>
          </div>
        )}
      </section>

      {message && (
        <div className="tem-notice tem-success">
          {message}
        </div>
      )}

      {error && (
        <div className="tem-notice tem-error">
          {error}
        </div>
      )}

      <section className="tem-metrics">
        <div>
          <span>Active devices</span>
          <strong>{activeDevices.length}</strong>
        </div>

        <div>
          <span>Windows</span>
          <strong>{windowsDevices.length}</strong>
        </div>

        <div>
          <span>macOS</span>
          <strong>{macDevices.length}</strong>
        </div>

        <div>
          <span>Enrollment keys</span>
          <strong>{keys.length}</strong>
        </div>
      </section>

      <section className="tem-card">
        <div className="tem-section-head">
          <div>
            <span>ENROLLMENT CREDENTIALS</span>
            <h2>Company Keys</h2>
          </div>

          <div className="tem-create">
            <label>
              Device limit
              <input
                type="number"
                min="1"
                value={maxDevices}
                onChange={(event) =>
                  setMaxDevices(event.target.value)
                }
              />
            </label>

            <button
              onClick={createKey}
              disabled={!companyId || loading}
            >
              Generate Key
            </button>
          </div>
        </div>

        {oneTimeKey && (
          <div className="tem-secret">
            <span>{oneTimeKeyLabel}</span>

            <strong>{oneTimeKey}</strong>

            <div>
              <button onClick={copyKey}>
                Copy Key
              </button>

              <button
                className="tem-secondary"
                onClick={() => setOneTimeKey("")}
              >
                Hide
              </button>
            </div>

            <small>
              Save this value securely. Normal key listings
              expose only the fingerprint.
            </small>
          </div>
        )}

        <div className="tem-table-wrap">
          <table className="tem-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Fingerprint</th>
                <th>Uses</th>
                <th>Limit</th>
                <th>Last used</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && keys.length === 0 && (
                <tr>
                  <td colSpan="8">
                    No enrollment keys for this company.
                  </td>
                </tr>
              )}

              {keys.map((key) => (
                <tr key={key.id}>
                  <td>v{key.version}</td>

                  <td>
                    <span
                      className={`tem-state tem-state-${String(
                        key.status || ""
                      ).toLowerCase()}`}
                    >
                      {key.status || "unknown"}
                    </span>
                  </td>

                  <td>
                    {key.key_fingerprint || "—"}
                  </td>

                  <td>{key.use_count ?? 0}</td>
                  <td>{key.max_devices ?? "—"}</td>

                  <td>
                    {formatDate(key.last_used_at)}
                  </td>

                  <td>
                    {formatDate(key.created_at)}
                  </td>

                  <td>
                    <div className="tem-actions">
                      {key.status === "active" && (
                        <>
                          <button
                            onClick={() =>
                              rotateKey(key)
                            }
                          >
                            Rotate
                          </button>

                          <button
                            className="tem-danger"
                            onClick={() =>
                              revokeKey(key)
                            }
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="tem-card">
        <div className="tem-section-head">
          <div>
            <span>TENANT DEVICE BOUNDARY</span>
            <h2>Managed Devices</h2>
          </div>
        </div>

        <div className="tem-device-grid">
          {selectedCompanyDevices.map((device) => (
            <div
              className="tem-device"
              key={device.device_uuid || device.id}
            >
              <strong>
                {device.hostname ||
                  device.device_name ||
                  "Endpoint"}
              </strong>

              <span>
                {device.platform || "Unknown platform"}
              </span>

              <small>
                {device.device_uuid}
              </small>

              <b>
                {device.lifecycle_state || "active"}
              </b>
            </div>
          ))}

          {selectedCompanyDevices.length === 0 && (
            <div className="tem-empty">
              No currently loaded devices for this company.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default TenantEnrollmentPage;
